import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  Input,
  InputSignal,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { SecurityType } from 'src/app/modules/admin/models';
import { SecurityTypeService } from 'src/app/modules/admin/services';

@Component({
  standalone: false,
  selector: 'gstar-security-types-combobox',
  templateUrl: './security-types-combobox.component.html',
  styleUrls: ['./security-types-combobox.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecurityTypesComboboxComponent implements OnInit {
  private readonly securityTypeService = inject(SecurityTypeService);
  private readonly translateService = inject(TranslateService);

  @Input() value!: SecurityType | null | undefined;

  disabled: InputSignal<boolean> = input(false);
  nameValue: InputSignal<string> = input('');
  onlyParent: InputSignal<boolean> = input(false);
  allowedSecurityTypeNames: InputSignal<string[]> = input<string[]>([]);
  required: InputSignal<boolean> = input(false);
  placeholder: InputSignal<string> = input(
    this.translateService.instant('upsertSecurity.securityType')
  );

  selectSecurityType = output<SecurityType | null | undefined>();
  clearSecurityType = output<void>();
  public securityTypes: WritableSignal<SecurityType[]> = signal<SecurityType[]>(
    []
  );

  ngOnInit(): void {
    this.securityTypeService.activeSecurityTypeList().subscribe(response => {
      const allowedNames = this.allowedSecurityTypeNames();
      const hasAllowedNames = allowedNames.length > 0;
      const allowedNameSet = new Set(
        allowedNames.map((name: string) => name.toLowerCase())
      );

      // Filter the security types based on allowed names and onlyParent flag
      // filteredItems could contain all the items.
      const filteredItems = response.items.filter((item: SecurityType) =>
        hasAllowedNames
          ? allowedNameSet.has(item.name.toLowerCase())
          : this.onlyParent()
            ? item.isParent
            : true
      );

      if (this.onlyParent()) {
        this.securityTypes.set(
          cloneDeep(filteredItems.filter((item: SecurityType) => item.isParent))
        );
      } else {
        this.securityTypes.set(cloneDeep(filteredItems));
      }
      if (this.nameValue()) {
        this.value = this.securityTypes().find(
          (item: SecurityType) => item.name === this.nameValue()
        );
      }
    });
  }

  selectSecurityTypeHandler(
    securityType: SecurityType | null | undefined
  ): void {
    this.value = securityType;
    this.selectSecurityType.emit(securityType);
  }

  clearSecurityTypeHandler(): void {
    this.value = null;
    this.clearSecurityType.emit();
  }
}
